import { createApp } from 'vue'
import { createHead } from '@unhead/vue'
import { DefaultApolloClient } from '@vue/apollo-composable'
import { ApolloClient } from '@apollo/client/core/ApolloClient.js'
import { InMemoryCache } from '@apollo/client/cache/inmemory/inMemoryCache.js'
import { createHttpLink } from '@apollo/client/link/http/createHttpLink.js'
import router from './routes'
import App from './App.vue'

// Import global styles
import './assets/scss/style.scss'

// Store redirects globally for navigation guard
let redirectsMap = new Map()
let redirectsLoaded = false
let redirectsPromise = null

// Function to setup redirects from Strapi
async function setupRedirects(router) {
  if (redirectsPromise) {
    return redirectsPromise
  }
  
  redirectsPromise = (async () => {
    try {
      const allRedirects = []
      let page = 1
      let hasMore = true
      const pageSize = 100 // Use max allowed page size
      
      // Fetch all pages of redirects
      while (hasMore) {
        const response = await fetch(`${import.meta.env.VITE_API_URL}/api/redirects?pagination[page]=${page}&pagination[pageSize]=${pageSize}`)
        const redirectsData = await response.json()
        
        // Handle the redirects array from your API response format
        const redirects = redirectsData.data || redirectsData.redirects || []
        const meta = redirectsData.meta || {}
        
        allRedirects.push(...redirects)
        
        // Check if there are more pages
        if (meta.pagination) {
          hasMore = page < meta.pagination.pageCount
        } else {
          // Fallback: if we got fewer redirects than page size, assume we're done
          hasMore = redirects.length === pageSize
        }
        
        page++
      }
      
      // Store redirects in a map for quick lookup
      allRedirects.forEach(redirect => {
        const { from, to, type } = redirect.attributes || redirect
        redirectsMap.set(from, { to, type })
      })
      
      console.log(`✅ Loaded ${allRedirects.length} redirects`)
      redirectsLoaded = true
      
    } catch (error) {
      console.error('❌ Failed to load redirects:', error)
      redirectsLoaded = true // Set to true even on error to prevent infinite waiting
    }
  })()
  
  return redirectsPromise
}

// Navigation guard to handle redirects
function setupRedirectGuard(router) {
  router.beforeEach(async (to, from, next) => {
    // Wait for redirects to load if they haven't loaded yet
    if (!redirectsLoaded) {
      await redirectsPromise
    }
    
    const redirect = redirectsMap.get(to.path)
    
    if (redirect) {
      const { to: redirectTo } = redirect
      
      // Check if it's an absolute URL
      if (redirectTo.startsWith('http://') || redirectTo.startsWith('https://')) {
        try {
          const toUrl = new URL(redirectTo)
          const currentOrigin = window.location.origin
          
          // If it's the same origin, redirect internally
          if (toUrl.origin === currentOrigin) {
            return next(toUrl.pathname + toUrl.search + toUrl.hash)
          } else {
            // For external URLs, perform full page redirect
            window.location.href = redirectTo
            return
          }
        } catch (urlError) {
          console.warn(`Invalid URL in redirect: ${redirectTo}`, urlError)
        }
      } else {
        // For relative paths, redirect internally
        return next(redirectTo)
      }
    }
    
    // No redirect found, continue normally
    next()
  })
}

// Create the app instance
const app = createApp(App)

// Set up Apollo Client
const httpLink = createHttpLink({
  uri: import.meta.env.VITE_API_URL + '/graphql'
})

const cache = new InMemoryCache()

const apolloClient = new ApolloClient({
  link: httpLink,
  cache,
  connectToDevTools: true
})

// Provide Apollo client to all components
app.provide(DefaultApolloClient, apolloClient)

// Use the existing router
app.use(router)

// Set up the redirect navigation guard
setupRedirectGuard(router)

// Set up head for metadata
const head = createHead()
app.use(head)

// Setup redirects and wait for router to be ready
Promise.all([
  setupRedirects(router),
  router.isReady()
]).then(() => {
  // Mount the app after both redirects are loaded and router is ready
  app.mount('#app')
}).catch(error => {
  console.error('Failed to initialize app:', error)
  // Mount anyway to prevent blank page
  app.mount('#app')
}) 